import { keepoLanguageOf } from "../services/i18ln/languages";
import { uuidv4 } from "../utils/uuid";
import { ItemSectionLinks, Section, Site, SitePage } from "./site";

/** The same rules as the API (api-keepo-v2 common/components/KeepoPages.php): keep them in sync. */
export const MAX_PAGES = 10;
export const SLUG_MAX = 60;
export const TITLE_MAX = 60;
export const RESERVED_SLUGS = ["assets", "build", "static", "api", "admin", "index", "home", "inertia"];

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export type PageError = "titleRequired" | "slugRequired" | "slugInvalid" | "slugReserved" | "slugTaken";

/**
 * The menu's home, as the published page names it: in the account's language (the API's DEPLOY_SITE_MENU_HOME,
 * Portuguese or English, see keepoLanguageOf), not the editor's.
 */
export const homeTitleFor = (accountLang?: string): string =>
  keepoLanguageOf(accountLang) === "en" ? "Home" : "Início";

/** An entry of the published page's menu (the renderer's `site.navigation`). */
export type NavigationItem = { title: string; href: string; current: boolean };

/** "Quem Somos" → "quem-somos": no accents, symbols or repeated hyphens. */
export const slugify = (title: string): string =>
  title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX)
    .replace(/-+$/, "");

/** The title as the API keeps it (PHP's strip_tags: an unclosed `<` takes the rest), "<3" → "". */
const stripTags = (title: string) => title.replace(/<[^>]*(>|$)/g, "").trim();

export const validatePage = (
  { title, slug }: { title: string; slug: string },
  pages: SitePage[],
  editingId?: string
): PageError | null => {
  if (!stripTags(title)) return "titleRequired";
  if (!slug) return "slugRequired";
  if (slug.length > SLUG_MAX || !SLUG_PATTERN.test(slug)) return "slugInvalid";
  if (RESERVED_SLUGS.includes(slug)) return "slugReserved";
  if (pages.some(page => page.slug === slug && page.id !== editingId)) return "slugTaken";
  return null;
};

/** Whether the menu can be on: some page besides the home is in it (only the home, or every page hidden: no). */
export const canHaveMenu = (pages: SitePage[]): boolean => pages.some(({ showInMenu }) => showInMenu);

/**
 * The Keepo with these pages, and the menu following them: off when no page is left in it (only the home, or every
 * page hidden), on when the first one comes into it (a new page, or one shown again); in between it stays as the
 * owner set it (turning it off is theirs).
 */
export const withPages = (site: Site, pages: SitePage[]): Site => {
  const before = canHaveMenu(site.pages ?? []);
  const after = canHaveMenu(pages);
  const menu = !after ? { enabled: false } : !before ? { enabled: true } : site.menu;
  return { ...site, pages, ...(menu && { menu }) };
};

/** A free address for a copy, within SLUG_MAX: `contato-copia`, then `contato-copia-2`... */
const copySlug = (slug: string, pages: SitePage[]) => {
  const base = slugify(`${slug}-copia`);
  let candidate = base;
  for (let n = 2; pages.some(page => page.slug === candidate); n++) {
    const suffix = `-${n}`;
    candidate = `${base.slice(0, SLUG_MAX - suffix.length).replace(/-+$/, "")}${suffix}`;
  }
  return candidate;
};

/** A copy of a section, with new ids down to its items. */
export const duplicateSection = (section: Section): Section => {
  const { items } = section as Section & { items?: unknown };
  return {
    ...section,
    id: uuidv4(),
    ...(Array.isArray(items) && { items: items.map(item => ({ ...item, id: uuidv4() })) }),
  };
};

/** A copy of `page` to add to `pages`: "<title> <copySuffix>", a free address, new ids down to the items. */
export const duplicatePage = (page: SitePage, pages: SitePage[], copySuffix: string): SitePage => ({
  ...page,
  id: uuidv4(),
  title: `${page.title} ${copySuffix}`.slice(0, TITLE_MAX),
  slug: copySlug(page.slug, pages),
  sections: page.sections.map(duplicateSection),
});

/**
 * The home as a new page to add to `pages` ("<homeTitle> <copySuffix>", in the menu): its sections copied, but its
 * welcome (only the home has one).
 */
export const duplicateHome = (site: Site, pages: SitePage[], homeTitle: string, copySuffix: string): SitePage =>
  duplicatePage(
    {
      id: "",
      title: homeTitle,
      slug: slugify(homeTitle) || "inicio",
      showInMenu: true,
      sections: site.sections.filter(({ type }) => type !== "welcome"),
    },
    pages,
    copySuffix
  );

/**
 * Whether a section can go to the page `pageId` (null: the home): the welcome stays on the home, and a section that
 * can only be once on a page can't go to one that already has it. `isMultiple`: whether one more of it is allowed.
 */
export const canMoveSection = (
  site: Site,
  section: Section,
  pageId: string | null,
  isMultiple: boolean
): "ok" | "welcome" | "already" => {
  if (section.type === "welcome") return "welcome";
  const there = getPageSections(site, pageId);
  if (!isMultiple && there.some(({ type, subType }) => type === section.type && subType === section.subType)) {
    return "already";
  }
  return "ok";
};

/** Moves a section from the page `from` to the end of the page `to` (null: the home), in `site` (its copy). */
export const moveSection = (site: Site, from: string | null, sectionId: string, to: string | null): void => {
  const source = getPageSections(site, from);
  const index = source.findIndex(({ id }) => id === sectionId);
  if (index < 0) return;
  const section = source[index];
  // new arrays: the ones the site had are left as they were
  setPageSections(
    site,
    from,
    source.filter((_, i) => i !== index)
  );
  setPageSections(site, to, [...getPageSections(site, to), section]);
};

export const getPages = (site: Site): SitePage[] => site.pages ?? [];

/** Whether the draft already has something built: sections on the home or any page (a new Keepo has none). */
export const siteHasContent = (site: Site | undefined): boolean =>
  !!site && ((site.sections?.length ?? 0) > 0 || getPages(site).length > 0);

export const findPage = (site: Site, pageId: string | null): SitePage | undefined =>
  pageId ? getPages(site).find(({ id }) => id === pageId) : undefined;

/**
 * The page a link of the published Keepo (`/` or `/<slug>`, the menu's) leads to: its id, null for the home, or
 * undefined when it leads to none (an unknown address; no pages without PLUS).
 */
export const pageIdForPath = (site: Site, isPlus: boolean, path: string): string | null | undefined => {
  if (path === "/") return null;
  return isPlus ? getPages(site).find(({ slug }) => `/${slug}` === path)?.id : undefined;
};

/** `address` (the Keepo's) followed by the page's own, as the published page is reached; the home has none. */
export const pageAddress = (address: string, site: Site, isPlus: boolean, pageId: string | null): string => {
  const page = isPlus ? findPage(site, pageId) : undefined;
  return page ? `${address}/${page.slug}` : address;
};

/** The sections being edited: the page's, or the home's. The live array, as the editor mutates it. */
export const getPageSections = (site: Site, pageId: string | null): Section[] =>
  findPage(site, pageId)?.sections ?? site.sections;

export const setPageSections = (site: Site, pageId: string | null, sections: Section[]): void => {
  const page = findPage(site, pageId);
  if (page) {
    page.sections = sections;
  } else {
    site.sections = sections;
  }
};

type WithPageLink = Section & { pageId?: string; link?: string; items?: ItemSectionLinks[] };

/** pageId → `/slug`; a link to a page that's gone leaves (as the API does when publishing). */
const resolvePageLinks = (sections: Section[], pages: SitePage[]): Section[] => {
  const hrefs = new Map(pages.map(({ id, slug }) => [id, `/${slug}`]));

  return sections.flatMap(original => {
    const section = { ...original } as WithPageLink;
    if (section.type === "button" && section.pageId) {
      const href = hrefs.get(section.pageId);
      if (!href) return [];
      section.link = href;
    }
    if (section.type === "links" && section.items) {
      section.items = section.items.flatMap(item => {
        if (!item.pageId) return [item];
        const href = hrefs.get(item.pageId);
        return href ? [{ ...item, link: href }] : [];
      });
    }
    return [section];
  });
};

/**
 * The Keepo as the published page shows `pageId` (null: the home): its sections, links to pages resolved and
 * the menu, mirroring KeepoPages::applyPage in the API. Pages are PLUS. A copy: `site` is left as it was.
 */
export const pageView = (
  site: Site,
  pageId: string | null,
  isPlus: boolean,
  homeTitle: string
): Site & { navigation: NavigationItem[] } => {
  const pages = isPlus ? getPages(site) : [];
  const page = pageId ? pages.find(({ id }) => id === pageId) : undefined;
  const shown = pages.filter(({ showInMenu }) => showInMenu);

  const navigation: NavigationItem[] =
    site.menu?.enabled && shown.length
      ? [
          { title: homeTitle, href: "/", current: !page },
          ...shown.map(({ id, slug, title }) => ({ title, href: `/${slug}`, current: id === page?.id })),
        ]
      : [];

  return {
    ...site,
    sections: resolvePageLinks(page ? page.sections : site.sections, pages),
    navigation,
  };
};
